import type { EventStatus, PaymentStatus, SchedulerEvent, SchedulerView } from './types'

// ─── Display Constants ────────────────────────────────────────────────────────

export const NOW_INTERVAL_MS = 30_000
export const MIN_EVENT_H = 28
export const TALL_THRESHOLD = 52
export const EXTRA_TALL_THRESHOLD = 80
export const FULL_PCT = 100
export const TIME_AXIS_W = 72
export const COL_MIN_W = 150
export const MINS_PER_HOUR = 60
export const DAYS_PER_WEEK = 7
export const SATURDAY_INDEX = 6
export const ANIMATE_DURATION = 0.12
export const OCCUPANCY_HIGH = 90
export const OCCUPANCY_MED = 60
export const MIN_BLOCKED_H = 20
export const AUTO_SCROLL_OFFSET = 80
export const TIME_LABEL_H = 20

// ─── Mobile Constants ─────────────────────────────────────────────────────────

/** px height per 30-min slot when empty */
export const MOBILE_SLOT_H = 56
/** px to subtract from scroll target on auto-scroll */
export const MOBILE_AUTO_SCROLL_OFFSET = 120
/** Number of skeleton rows shown while loading on mobile */
export const MOBILE_SKELETON_ROWS = 8
export const EVENT_CARD_HEIGHT_OFFSET = 3
export const MOBILE_PLACEHOLDER_EVERY_NTH_ROW = 3
export const MOBILE_EVENT_CARD_VERTICAL_PADDING = 8
export const MOBILE_EVENT_OVERLAP_TOTAL_WIDTH_PCT = 100
export const MIN_CLICKABLE_SLOT_HEIGHT = 36
export const MOBILE_BLOCKED_CARD_VERTICAL_OFFSET = 4

// ─── View Controls ────────────────────────────────────────────────────────────

export const SCHEDULER_VIEW_LABELS: Record<SchedulerView, string> = {
  day: 'Dia',
  week: 'Semana',
  month: 'Mês',
} as const

export const SCHEDULER_VIEW_OPTIONS: SchedulerView[] = ['day', 'week']

// ─── Styling Maps ─────────────────────────────────────────────────────────────

export const VARIANT_ACCENT: Record<NonNullable<SchedulerEvent['variant']>, string> = {
  default: 'border-l-primary',
  success: 'border-l-success',
  warning: 'border-l-warning',
  error: 'border-l-error',
  ghost: 'border-l-border-strong',
} as const

export const VARIANT_BASE: Record<NonNullable<SchedulerEvent['variant']>, string> = {
  default: 'bg-primary/10 text-primary dark:bg-primary/15',
  success: 'bg-success/10 text-success dark:bg-success/15',
  warning: 'bg-warning/10 text-warning dark:bg-warning/15',
  error: 'bg-error/10 text-error dark:bg-error/15',
  ghost: 'bg-bg-elevated text-text-secondary',
} as const

// ─── Status Maps ──────────────────────────────────────────────────────────────

/**
 * Border-left accent class derived from event status.
 * For pending status, use STATUS_ACCENT_RECURRING when isRecurring is true.
 */
export const STATUS_ACCENT: Record<EventStatus, string> = {
  pending: 'border-l-secondary/60',
  confirmed: 'border-l-success',
  canceled: 'border-l-border-strong',
  'no-show': 'border-l-error',
} as const

/** Border-left accent for recurring pending events (deeper blue) */
export const STATUS_ACCENT_RECURRING = 'border-l-secondary'

/**
 * Background + text class derived from event status.
 * For pending status, use STATUS_BASE_RECURRING when isRecurring is true.
 */
export const STATUS_BASE: Record<EventStatus, string> = {
  pending: 'bg-secondary/10 text-secondary dark:bg-secondary/15',
  confirmed: 'bg-success/10 text-success dark:bg-success/15',
  canceled: 'bg-bg-elevated text-text-secondary',
  'no-show': 'bg-error/10 text-error dark:bg-error/15',
} as const

/** Background + text for recurring pending events (deeper blue shade) */
export const STATUS_BASE_RECURRING = 'bg-secondary/20 text-secondary dark:bg-secondary/25'

export const STATUS_OPACITY: Record<EventStatus, string> = {
  confirmed: '',
  pending: '',
  canceled: 'opacity-50',
  'no-show': '',
} as const

export const STATUS_TITLE_DECOR: Record<EventStatus, string> = {
  confirmed: '',
  pending: '',
  canceled: 'line-through',
  'no-show': 'line-through',
} as const

export const STATUS_LABEL_PT: Record<EventStatus, string> = {
  confirmed: 'Finalizado',
  pending: 'Em aberto',
  canceled: 'Cancelado',
  'no-show': 'Não compareceu',
} as const

export const STATUS_COLOR_CLASS: Record<EventStatus, string> = {
  confirmed: 'text-success',
  pending: 'text-secondary',
  canceled: 'text-text-tertiary',
  'no-show': 'text-error',
} as const

// ─── Payment Maps ─────────────────────────────────────────────────────────────

export const PAYMENT_DOT: Record<PaymentStatus, string> = {
  paid: 'bg-success',
  pending: 'bg-warning',
  overdue: 'bg-error',
} as const

export const PAYMENT_LABEL_PT: Record<PaymentStatus, string> = {
  paid: 'Pago',
  pending: 'Pagamento pendente',
  overdue: 'Pagamento em atraso',
} as const

export const MONTH_PT = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const
